import { AxiosError } from 'axios';

interface ApiErrorBody {
  message?: string;
  error?: string;
  errors?: Record<string, string[] | string> | string[] | string;
}

// upstack_api's render_error puts this status line in `message` and the real reason in `errors`.
const STATUS_LINE = /^Request failed - /;

export const extractApiErrorMessage = (
  error: { message: string; errorResponse?: AxiosError } | null | undefined,
  fallback: string
): string => {
  if (!error) return fallback;
  const responseData = error.errorResponse?.response?.data as ApiErrorBody | undefined;
  // No response at all: axios's own words distinguish "never landed" from "was refused", which the fallback cannot.
  if (!responseData) return error.message || fallback;

  const message = typeof responseData.message === 'string' ? responseData.message.trim() : '';
  if (message && !STATUS_LINE.test(message)) return message;

  const flattened = flattenErrors(responseData.errors);
  if (flattened) return flattened;
  if (message) return message;

  // The server answered but said nothing usable, so axios's message here is only ever its status line -
  // "Request failed with status code 500" is worse for a customer than the caller's own wording.
  return fallback;
};

// More than one validator can reject a field for the same reason, e.g. a taken email.
const unique = (messages: string[]): string[] => Array.from(new Set(messages));

const flattenErrors = (errors: ApiErrorBody['errors']): string | null => {
  if (!errors) return null;
  if (typeof errors === 'string') return errors.trim() || null;
  if (Array.isArray(errors)) return errors.join(', ') || null;
  // Rails sends per-attribute lists and full_messages together; a blind join yields '[object Object]'.
  const fullMessages: unknown = errors.full_messages;
  if (Array.isArray(fullMessages)) {
    const sentences = fullMessages.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
    if (sentences.length > 0) return unique(sentences).join(', ');
  }
  const parts: string[] = [];
  for (const [field, value] of Object.entries(errors)) {
    if (field === 'full_messages') continue;
    const text = Array.isArray(value) ? unique(value).join(', ') : value;
    if (text) parts.push(`${field} ${text}`);
  }
  return parts.length > 0 ? parts.join('; ') : null;
};
